import { useCallback, useState } from 'react'
import { CreateLabelRequestLabel, UpdateLabelRequestLabel } from '@noco/http-client/lib/noco'
import { Toaster } from 'src/components/atoms'
import { handleError } from 'src/fixtures/utils/error'
import useSWR, { useSWRConfig } from 'swr'
import { useAuthenticate } from '../auth/hooks'
import { nocoSDK } from '../initialize'
import { SWRCachePath } from '../swr-cach-path'

nocoSDK.initalize()
const labelApi = nocoSDK.client?.userService.labelApi

/**
 * ラベル一覧を取得する
 */
export const useListLabels = () => {
  const { data: auth } = useAuthenticate()
  const func = useCallback(async () => {
    if (!auth) return
    return await labelApi
      ?.ApiFactory(auth.token)
      .userV1LabelsGet()
      .then(res => res.data)
  }, [auth])
  return useSWR(auth && SWRCachePath.listLabels(), func, {
    onError: err => handleError(err, { tag: 'List labels' })
  })
}

/**
 * ラベル詳細を取得する
 */
export const useGetLabel = (labelId?: string) => {
  const { data: auth } = useAuthenticate()
  const func = useCallback(async () => {
    if (!auth || !labelId) return undefined
    return await labelApi
      ?.ApiFactory(auth.token)
      .userV1LabelsLabelIdGet({ labelId })
      .then(res => res.data)
  }, [auth, labelId])
  return useSWR(auth && labelId && SWRCachePath.getLabel(labelId), func, {
    onError: err => handleError(err, { tag: 'Get label' })
  })
}

/**
 * ラベルを作成する
 */
export const useCreateLabel = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate } = useSWRConfig()

  const handleCreateLabel = useCallback(
    async (label: CreateLabelRequestLabel) => {
      try {
        if (!auth) return
        setError(undefined)
        setIsLoading(true)
        const res = await labelApi?.ApiFactory(auth.token).userV1LabelsPost({ createLabelRequest: { label } })
        await mutate(SWRCachePath.listLabels())
        Toaster.success('ラベルを作成しました')
        return res?.data
      } catch (err) {
        await handleError(err, { setError, tag: 'Create label' })
      } finally {
        setIsLoading(false)
      }
    },
    [auth, mutate]
  )

  return { handleCreateLabel, isLoading, error }
}

/**
 * ラベルを更新する
 */
export const useUpdateLabel = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate } = useSWRConfig()

  const handleUpdateLabel = useCallback(
    async (labelId: string, label: UpdateLabelRequestLabel) => {
      try {
        if (!auth) return
        setError(undefined)
        setIsLoading(true)
        const res = await labelApi
          ?.ApiFactory(auth.token)
          .userV1LabelsLabelIdPut({ labelId, updateLabelRequest: { label } })
        await mutate(SWRCachePath.listLabels())
        await mutate(SWRCachePath.getLabel(labelId))
        Toaster.success('ラベルを更新しました')
        return res?.data
      } catch (err) {
        await handleError(err, { setError, tag: 'Update label' })
      } finally {
        setIsLoading(false)
      }
    },
    [auth, mutate]
  )

  return { handleUpdateLabel, isLoading, error }
}

/**
 * ラベルを削除する
 */
export const useDeleteLabel = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate } = useSWRConfig()

  const handleDeleteLabel = useCallback(
    async (labelId: string): Promise<boolean> => {
      try {
        if (!auth) return false
        setError(undefined)
        setIsLoading(true)
        await labelApi?.ApiFactory(auth.token).userV1LabelsLabelIdDelete({ labelId })
        await mutate(SWRCachePath.listLabels())
        Toaster.success('ラベルを削除しました')
        return true
      } catch (err) {
        await handleError(err, { setError, tag: 'Delete label' })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [auth, mutate]
  )

  return { handleDeleteLabel, isLoading, error }
}

/**
 * ラベルの並び順を更新する
 */
export const useSortLabels = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate } = useSWRConfig()

  const handleSortLabels = useCallback(
    async (labels: Array<{ id: string; sort: number }>) => {
      try {
        if (!auth) return
        setError(undefined)
        setIsLoading(true)
        const res = await labelApi?.ApiFactory(auth.token).userV1LabelsSortPut({ sortLabelsRequest: { labels } })
        await mutate(SWRCachePath.listLabels())
        Toaster.success('並び順を更新しました')
        return res?.data
      } catch (err) {
        await handleError(err, { setError, tag: 'Sort labels' })
      } finally {
        setIsLoading(false)
      }
    },
    [auth, mutate]
  )

  return { handleSortLabels, isLoading, error }
}
